import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal';
import { PATHS } from '../../routes/paths';
import { getShopListApi, deleteShopApi, getServerBaseUrl, getShopCategoriesApi } from '../../api';

const ShopManagement = () => {
  const [currentUser, setCurrentUser] = React.useState(null);
  const [shops, setShops] = React.useState([]);
  const [keyword, setKeyword] = React.useState('');
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [deleteTargetShop, setDeleteTargetShop] = React.useState(null);
  const [categories, setCategories] = React.useState([]);

  const navigate = useNavigate();

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        setCurrentUser(JSON.parse(raw));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  const isAdmin = currentUser?.role === 'ADMIN';

  const serverUrl = React.useMemo(() => getServerBaseUrl(), []);

  const getThumbnailUrl = (path) => {
    if (!path) return '/image/hotel1_image.png';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${serverUrl}${path}`;
  };

  const loadCategories = React.useCallback(async () => {
    try {
      const res = await getShopCategoriesApi();
      const list = Array.isArray(res?.data) ? res.data : res;
      const normalized = (list || []).map((item) => ({
        id: item.categoryId ?? item.category_id ?? item.id,
        label: item.name,
        sortOrder: item.sortOrder ?? item.sort_order ?? 0,
      }));
      normalized.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategories(normalized);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load shop categories:', err);
    }
  }, []);

  const fetchShops = React.useCallback(
    async () => {
      try {
        const categoryId = activeCategory === 'all' ? undefined : activeCategory;

        const res = await getShopListApi({
          page: 1,
          limit: 100,
          categoryId,
        });
        const items = res.data?.items || [];
        if (!keyword.trim()) {
          setShops(items);
        } else {
          const lower = keyword.trim().toLowerCase();
          setShops(items.filter((s) => s.name?.toLowerCase().includes(lower)));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    [keyword, activeCategory]
  );

  React.useEffect(() => {
    if (!isAdmin) return;
    loadCategories();
    fetchShops();
  }, [fetchShops, isAdmin, loadCategories]);

  React.useEffect(() => {
    if (activeCategory === 'all') return;
    const exists = categories.some((item) => String(item.id) === String(activeCategory));
    if (!exists) {
      setActiveCategory('all');
    }
  }, [activeCategory, categories]);

  const handleClickRegister = () => {
    navigate(PATHS.shopRegister);
  };

  if (!isAdmin) {
    return (
      <Box>
        <Box
          sx={{
            maxWidth: 430,
            mx: 'auto',
            bgcolor: 'white',
            minHeight: '100vh',
            borderRadius: 0,
            boxShadow: '0 0 20px rgba(0,0,0,0.06)',
            px: 2,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 60,
              borderBottom: '1px solid #e0e0e0',
              mb: 3,
              mx: -2,
              px: 2,
              position: 'relative',
            }}
          >
            <IconButton onClick={() => navigate(PATHS.shops)} sx={{ p: 0, position: 'absolute', left: 16 }}>
              <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
            </IconButton>
            <Typography
              sx={{
                flex: 1,
                textAlign: 'center',
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              업체 관리
            </Typography>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" sx={{ fontSize: 14 }}>
              관리자만 접근할 수 있는 페이지입니다.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            borderBottom: '1px solid #e0e0e0',
            mb: 3,
            mx: -2,
            px: 2,
            position: 'relative',
          }}
        >
          <IconButton onClick={() => navigate(PATHS.shops)} sx={{ p: 0, position: 'absolute', left: 16 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            업체 관리
          </Typography>
        </Box>

        <Box sx={{ mt: 1.5, mb: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {[{ key: 'all', label: '전체' }, ...categories.map((c) => ({ key: c.id, label: c.label }))].map((item) => {
              const selected = activeCategory === item.key;
              return (
                <Button
                  key={item.key}
                  variant={selected ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setActiveCategory(item.key)}
                  sx={{
                    minWidth: 0,
                    px: 1.6,
                    py: 0.3,
                    fontSize: 13,
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                    boxShadow: selected ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                    bgcolor: selected ? '#111827' : '#ffffff',
                    color: selected ? '#ffffff' : '#111827',
                    borderColor: '#e5e7eb',
                    '&:hover': {
                      bgcolor: selected ? '#111827' : '#f9fafb',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="검색어를 입력해주세요."
              size="small"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 20, color: '#9ca3af', mr: 0.5 }} />,
                sx: {
                  borderRadius: 2,
                  fontSize: 13,
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 8 }}>
            {shops.map((shop) => (
              <Paper
                key={shop.shopId}
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 2,
                  border: '1px solid #e5e5e5',
                  p: 1.2,
                  gap: 1.2,
                }}
              >
                <Box
                  component="img"
                  src={getThumbnailUrl(shop.thumbnailUrl)}
                  alt={shop.name}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13, mb: 0.3 }}>
                    {shop.name}
                  </Typography>
                <Typography variant="body2" sx={{ fontSize: 11, color: '#6b7280' }}>
                    {shop.categoryName || '업체 소개'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 56,
                      fontSize: 12,
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.2,
                    }}
                    onClick={() => {
                      navigate(PATHS.shopRegister, {
                        state: { mode: 'edit', shop },
                      });
                    }}
                  >
                    수정
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{
                      minWidth: 56,
                      fontSize: 12,
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.2,
                    }}
                    onClick={() => setDeleteTargetShop(shop)}
                  >
                    삭제
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        <IconButton
          onClick={handleClickRegister}
          sx={{
            position: 'fixed',
            bottom: 50,
            right: 'max(24px, calc((100vw - 430px) / 2 + 24px))',
            bgcolor: '#3679FF',
            color: '#ffffff',
            boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
            width: 48,
            height: 48,
            '&:hover': {
              bgcolor: '#245ad4',
            },
          }}
        >
          <AddIcon />
        </IconButton>

        <ConfirmModal
          open={Boolean(deleteTargetShop)}
          message="해당 업체를 삭제하시겠습니까?"
          confirmText="삭제"
          cancelText="취소"
          onCancel={() => setDeleteTargetShop(null)}
          onConfirm={async () => {
            if (!deleteTargetShop) return;
            try {
              await deleteShopApi(deleteTargetShop.shopId);
              setDeleteTargetShop(null);
              await fetchShops();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(err);
              if (err?.status === 401 || err?.suppressAlert) return;
              // eslint-disable-next-line no-alert
              window.alert(err.message || '업체 삭제 중 오류가 발생했습니다.');
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default ShopManagement;


